import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, mapTo, mergeMap, take, tap } from 'rxjs/operators';
import { get, setWith, unset } from 'lodash';
import { BridgeService } from 'crimson/lib/bridge/bridge.service';
import { DbService } from 'crimson/lib/db/db.service';
import { PlatformDetectorService } from 'crimson/lib/platform-detector/platform-detector.service';
import { Commands } from 'bridge-commands/commands';
import { Types as CommandTypes } from 'bridge-commands/types';

import OperationTypes = CommandTypes.Common.Sharedstorage.OperationType;

@Injectable({
    providedIn: 'root'
})
export class IndexedDBUtilService {
    private objectStoreName: string;
    constructor(
        private dbService: DbService,
        private bridgeService: BridgeService
    ) {}

    public initialize(objectStoreName?: string): void {
        if (this.objectStoreName) {
            throw new Error('ObjectStoreName already initialized');
        }
        if (objectStoreName) {
            this.objectStoreName = objectStoreName;
        } else {
            this.objectStoreName = 'sharedStore';
        }
    }

    public readFromSharedStorage(path: string[]): Observable<any> {
        return this.dbService
            .getGlobalStore(this.objectStoreName)
            .getItem(path[0])
            .pipe(
                mergeMap(response => {
                    if (!response) {
                        return this.makeError(204, 'Data not found');
                    }
                    if (path.length === 1) {
                        return of(response);
                    } else {
                        // Set path starting from index 1 as 0th index is used for key
                        const data = get(response, path.slice(1), null);
                        return data !== null ? of(data) : this.makeError(204, 'Data not found');
                    }
                }),
                catchError(err => this.makeError(500, 'IndexedDB Error: ' + JSON.stringify(err)))
            );
    }

    public writeToSharedStorage(path: string[], dataObject: object): Observable<boolean> {
        return this.dbService
            .getGlobalStore(this.objectStoreName)
            .getItem(path[0])
            .pipe(
                map(response => {
                    const existingData = response || {};
                    return path.length === 1 ? dataObject : setWith(existingData, path.slice(1), dataObject, Object);
                }),
                mergeMap(updatedData => {
                    return this.executePostCacheUpdateCommand(OperationTypes.Write, path, updatedData).pipe(
                        map(() => updatedData)
                    );
                }),
                mergeMap(updatedData => {
                    return this.dbService.getGlobalStore(this.objectStoreName).setItem(path[0], updatedData);
                }),
                mergeMap(() => of(true)),
                catchError(err => this.makeError(500, 'IndexedDB Error during write: ' + JSON.stringify(err)))
            );
    }

    public removeFromSharedStorage(path: string[]): Observable<boolean> {
        return this.dbService
            .getGlobalStore(this.objectStoreName)
            .getItem(path[0])
            .pipe(
                mergeMap(response => {
                    let data = response || {};
                    let retVal$: Observable<any>;

                    if (path.length === 1) {
                        retVal$ = this.dbService.getGlobalStore(this.objectStoreName).removeItem(path[0]);
                    } else {
                        data = unset(data, path.slice(1));
                        retVal$ = this.dbService.getGlobalStore(this.objectStoreName).setItem(path[0], data);
                    }

                    return retVal$;
                }),
                mergeMap(() => {
                    return this.executePostCacheUpdateCommand(OperationTypes.Remove, path);
                }),
                catchError(err => this.makeError(500, 'IndexedDB Error during remove: ' + JSON.stringify(err)))
            );
    }

    public getCacheOfSharedStorage(): Observable<any> {
        return this.dbService
            .getGlobalStore(this.objectStoreName)
            .getAllItems()
            .pipe(
                mergeMap(response => {
                    return this.executePostCacheUpdateCommand(OperationTypes.Cache, [], response).pipe(
                        map(() => response)
                    );
                }),
                catchError(err => this.makeError(500, 'IndexedDB Error during cache operation: ' + JSON.stringify(err)))
            );
    }

    private executePostCacheUpdateCommand(
        operationType: OperationTypes,
        path: string[],
        data?: any
    ): Observable<boolean> {
        /* Platform-check-disable-next-line */
        if (PlatformDetectorService.isBrowserPlatform()) {
            return this.bridgeService
                .executeCommand(Commands.Common.Sharedstorage.PostCacheUpdate, {
                    operationType: operationType,
                    path,
                    cacheUpdateData: data ?? {}
                })
                .pipe(
                    take(1),
                    mapTo(true),
                    catchError(err =>
                        this.makeError(500, 'Error during execution of post-cache update command' + JSON.stringify(err))
                    )
                );
        }
        return of(true);
    }

    private makeError(code: number, message: string): Observable<never> {
        return throwError({
            success: false,
            code,
            error: message
        });
    }
}
